import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {

  const [selectedCountry, setSelectedCountry] = useState({
    name: 'India',
    currency: 'INR',
    flag: '🇮🇳'
  });

  const [exchangeRates, setExchangeRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchExchangeRates = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://v6.exchangerate-api.com/v6/53b688ddb56fb8af0fbe4f5e/latest/USD');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.result !== 'success') {
        throw new Error('Failed to fetch exchange rates');
      }

      // Convert USD-based rates to INR-based rates
      const inrRate = data.conversion_rates.INR;
      const newExchangeRates = {};

      // List of currencies we support
      const supportedCurrencies = [
        'INR', 'SGD', 'THB', 'AED', 'BHD', 'HKD', 'KWD', 'MYR', 'CNY', 'JPY',
        'KRW', 'MOP', 'TWD', 'CAD', 'USD', 'EUR', 'DKK', 'CHF', 'GBP', 'AUD',
        'RUB', 'ZAR'
      ];

      supportedCurrencies.forEach(currency => {
        if (data.conversion_rates[currency]) {
          // Convert from USD-based to INR-based: (currency/USD) / (INR/USD) = currency/INR
          newExchangeRates[currency] = data.conversion_rates[currency] / inrRate;
        }
      });

      // Ensure INR is always 1
      newExchangeRates.INR = 1;

      setExchangeRates(newExchangeRates);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching exchange rates:', err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchExchangeRates();

    // Optional: Refresh rates every hour (3600000 ms)
    const interval = setInterval(fetchExchangeRates, 3600000);

    return () => clearInterval(interval);
  }, []);


  const convertPrice = (priceInINR) => {
    if (!priceInINR || selectedCountry.currency === 'INR') {
      return priceInINR;
    }

    // Check if rates are loaded
    if (Object.keys(exchangeRates).length === 0) {
      return priceInINR; // Return original price if rates not loaded yet
    }

    const rate = exchangeRates[selectedCountry.currency];
    if (!rate) {
      console.warn(`Exchange rate not found for currency: ${selectedCountry.currency}`);
      return priceInINR;
    }

    return Math.round(priceInINR * rate * 100) / 100;
  };


  const formatPrice = (price) => {
    if (!price) return '0';

    const convertedPrice = convertPrice(price);
    const currencySymbols = {
      INR: '₹',
      USD: '$',
      EUR: '€',
      GBP: '£',
      SGD: 'S$',
      AUD: 'A$',
      CAD: 'C$',
      CHF: 'CHF',
      CNY: '¥',
      JPY: '¥',
      KRW: '₩',
      THB: '฿',
      AED: 'د.إ',
      BHD: '.د.ب',
      HKD: 'HK$',
      KWD: 'د.ك',
      MYR: 'RM',
      MOP: 'MOP$',
      TWD: 'NT$',
      DKK: 'kr',
      RUB: '₽',
      ZAR: 'R'
    };

    const symbol = currencySymbols[selectedCountry.currency] || selectedCountry.currency;

    // Use appropriate locale based on currency
    let locale = "en-US"; // default

    if (selectedCountry.currency === 'INR') {
      locale = "en-IN"; // India - 25,29,034.71
    } else if (selectedCountry.currency === 'AUD') {
      locale = "en-AU"; // Australia - 2,529,034.71
    } else if (selectedCountry.currency === 'BHD') {
      locale = "ar-BH"; // Bahrain - Arabic format
    } else if (selectedCountry.currency === 'EUR') {
      locale = "de-DE"; // Europe - 2.529.034,71
    } else if (selectedCountry.currency === 'CAD') {
      locale = "en-CA"; // Canada - 2,529,034.71
    } else if (selectedCountry.currency === 'CNY') {
      locale = "zh-CN"; // China - 2,529,034.71
    } else if (selectedCountry.currency === 'DKK') {
      locale = "da-DK"; // Denmark - 2.529.034,71
    } else if (selectedCountry.currency === 'HKD') {
      locale = "zh-HK"; // Hong Kong - 2,529,034.71
    } else if (selectedCountry.currency === 'JPY') {
      locale = "ja-JP"; // Japan - 2,529,035 (no decimals)
    } else if (selectedCountry.currency === 'KRW') {
      locale = "ko-KR"; // Korea - 2,529,035 (no decimals)
    } else if (selectedCountry.currency === 'KWD') {
      locale = "ar-KW"; // Kuwait - Arabic format
    } else if (selectedCountry.currency === 'CHF') {
      locale = "de-CH"; // Switzerland - 2'529'034.71
    } else if (selectedCountry.currency === 'MOP') {
      locale = "zh-MO"; // Macau - 2,529,034.71
    } else if (selectedCountry.currency === 'MYR') {
      locale = "ms-MY"; // Malaysia - 2,529,034.71
    } else if (selectedCountry.currency === 'RUB') {
      locale = "ru-RU"; // Russia - 2 529 034,71
    } else if (selectedCountry.currency === 'SGD') {
      locale = "en-SG"; // Singapore - 2,529,034.71
    } else if (selectedCountry.currency === 'ZAR') {
      locale = "en-ZA"; // South Africa - 2 529 034,71
    } else if (selectedCountry.currency === 'THB') {
      locale = "th-TH"; // Thailand - 2,529,034.71
    } else if (selectedCountry.currency === 'TWD') {
      locale = "zh-TW"; // Taiwan - 2,529,034.71
    } else if (selectedCountry.currency === 'AED') {
      locale = "ar-AE"; // UAE - Arabic format
    } else if (selectedCountry.currency === 'GBP') {
      locale = "en-GB"; // UK - 2,529,034.71
    } else if (selectedCountry.currency === 'USD') {
      locale = "en-US"; // USA - 2,529,034.71
    }

    return `${symbol} ${convertedPrice.toLocaleString(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };


  const refreshRates = () => {
    fetchExchangeRates();
  };


  return (
    <CurrencyContext.Provider value={{ selectedCountry, setSelectedCountry, convertPrice, formatPrice, exchangeRates, refreshRates, loading, error }}>
      {children}
    </CurrencyContext.Provider>
  );
};